import * as React from "react";
import { useActiveFBOs } from "../containers/ActiveFBOContainer";
import { useApi } from "../containers/ApiContainer";
import { HOLDING_AREA_ID } from "../screens/MultiHangar/HoldingArea";
import { HoldingAreaService } from "../services/HoldingAreaService";
import {
  GarageDoor,
  Hangar,
  HangarBay,
  MovableObstacle,
  Obstacle,
  Position,
  Stack,
  Tenant,
} from "../types";
import { fetchAircraftWithCache, fetchHangar } from "./api";
import { isLocationChangeMeaningful } from "./useHangar";
import { useStateHistory } from "./useMeaningfulStateHistory";
import useSyncToTable from "./useSyncToTable";

const hasMeaningfulChanges = (prev: Hangar[], next: Hangar[]) => {
  if (!prev || !next) {
    return true;
  }
  return prev.some((p, i) => isLocationChangeMeaningful(p, next[i]));
};

export const useMultipleHangars = (
  hangar_ids: string[],
  isReference: boolean
) => {
  const { postgrest } = useApi();
  const { activeFBO } = useActiveFBOs();
  const [ready, setReady] = React.useState<boolean>(false);
  const [hangars, setHangars, undo] = useStateHistory<Hangar[]>(
    null,
    hasMeaningfulChanges
  );
  const setHangar = React.useCallback(
    (hangar: Hangar) => {
      // update the hangar that's currently being modified
      // if the hangar moved a tenant into it, then remove that
      // tenant from all other hangars
      const hangarsTenantIds = hangar.stack.tenants.map((t) => t.id);
      const newHangars = hangars.map((h) => {
        if (h.id === hangar.id) {
          return hangar;
        }

        // remove any duplicate tenants. this should not happen but has apparently happened
        // at Maven in the past
        const tenants = h.stack.tenants
          .filter((t) => !hangarsTenantIds.includes(t.id))
          .filter((t, i, self) => self.findIndex((s) => s.id === t.id) === i);

        return {
          ...h,
          stack: {
            ...h.stack,
            tenants,
          },
        };
      });
      setHangars(newHangars);
    },
    [hangars, setHangars]
  );

  const realHangars = hangars?.filter((h) => h.id !== HOLDING_AREA_ID);
  const holdingArea = hangars?.find((h) => h.id === HOLDING_AREA_ID);

  // watches for "core" diffs on the hangar. width, depth, name, etc.
  useSyncToTable<Hangar[]>({
    thingToWatch: realHangars,
    tableName: "hangar",
  });

  // watch for stack
  const stacks: Stack[] = [];
  for (const hangar of realHangars ?? []) {
    for (const stack of hangar.stacks ?? []) {
      if (stack.id === hangar.stack.id) {
        stacks.push(hangar.stack);
      } else {
        stacks.push(stack);
      }
    }
  }

  useSyncToTable<Stack[]>({
    tableName: "stack",
    thingToWatch: Boolean(realHangars) ? stacks : null,
  });

  // core info for a tenant can be modified within the holding area. so we need to include these
  // in the sync
  const tenants = [
    ...stacks?.flatMap((stack) => stack.tenants),
    ...(holdingArea?.stack.tenants || []),
  ];
  useSyncToTable<Tenant[]>({
    // pass in null until we actually have data
    thingToWatch: Boolean(realHangars) ? tenants : null,
    tableName: "tenant",
    deleteFromDatabase: async () => {
      // we don't actually delete tenants. they just leave the hangar. we're still
      // tracking them in this file b/c we need to be able to edit their info from
      // the hangar.
    },
  });

  const movableObstacles =
    [...stacks?.map((s) => [...s.movableObstacles])].flat() ?? [];
  useSyncToTable<MovableObstacle[]>({
    thingToWatch: Boolean(realHangars) ? movableObstacles : null,
    tableName: "movable_obstacle",
    deleteFromDatabase: async () => {
      // we don't actually delete movable obstacles. they just leave the hangar. we're still
      // tracking them in this file b/c we need to be able to edit their info from
      // the hangar.
    },
  });

  const currentPositions: Position[] = [];
  for (const stack of stacks ?? []) {
    for (const tenant of stack.tenants ?? []) {
      currentPositions.push(tenant.position);
    }
    for (const movableObstacle of stack.movableObstacles ?? []) {
      currentPositions.push(movableObstacle.position);
    }
  }

  useSyncToTable<Position[]>({
    // pass in null until we actually have data
    thingToWatch: Boolean(realHangars) ? currentPositions : null,
    tableName: "position",
    // wow this is ugly. if i put this in the hook, it will cause a circular dependency
    // so i'm just going to put it here. life soldiers on.
    onUpdate: async (rows) => {
      const hangarsAffected = realHangars.filter((h) =>
        rows.some((r) => h.stack.tenants.some((t) => t.position.id === r.id))
      );
      await postgrest
        .from("hangar")
        .update({
          last_stacked: new Date().toISOString(),
        })
        .in(
          "id",
          hangarsAffected.map((h) => h.id)
        );
    },
    // Add debounce to ensure Stacks are saved before Positions
    debounce: 200,
  });

  const obstacles = Boolean(realHangars)
    ? [...realHangars?.map((h) => [...(h.obstacles ?? [])])].flat()
    : [];

  useSyncToTable<Obstacle[]>({
    thingToWatch: Boolean(realHangars) ? obstacles : null,
    tableName: "obstacle",
  });

  const garageDoors = Boolean(realHangars)
    ? [...realHangars?.map((h) => [...(h.garageDoors ?? [])])].flat()
    : [];
  useSyncToTable<GarageDoor[]>({
    thingToWatch: Boolean(realHangars) ? garageDoors : null,
    tableName: "garage_door",
  });

  const hangarBays = Boolean(realHangars)
    ? [...realHangars?.map((h) => [...(h.bays ?? [])])].flat()
    : [];
  useSyncToTable<HangarBay[]>({
    thingToWatch: Boolean(realHangars) ? hangarBays : null,
    tableName: "hangar_bay",
  });

  // ok let's grab the hangars we only do this once. changes made in memory will be
  // synced to the database via the useSyncToTable hook
  React.useEffect(() => {
    (async () => {
      const doit = hangar_ids.map(
        async (hangar_id) =>
          await fetchHangar(postgrest, activeFBO, hangar_id, isReference)
      );
      const zHangarsRaw = await Promise.all(doit);
      // Filter out undefined values (invalid/non-existent hangar IDs)
      const zHangars = zHangarsRaw.filter((h): h is Hangar => h !== undefined);

      // Fetch holding area entries from database
      let holdingAreaTenants: Tenant[] = [];
      if (activeFBO?.id) {
        try {
          const holdingAreaService = new HoldingAreaService(postgrest);
          const entries = await holdingAreaService.getActiveEntries(
            activeFBO.id
          );

          // Extract tenants from entries
          holdingAreaTenants = entries
            .filter((entry) => entry.tenant)
            .map((entry) => ({
              ...entry.tenant,
              selected: false, // Add missing required field
              // Ensure position is null for holding area
              position: {
                ...entry.tenant.position,
                x: null,
                y: null,
                angle: null,
                preferences: entry.tenant.position?.preferences || [], // Ensure preferences array exists
              },
            }));

          // Enrich aircraft data with geometry buffers (geom_scaled_buffer, ramp_geom_scaled_buffer)
          // These are computed via RPC calls and not included in the basic database query
          const aircraftIds = holdingAreaTenants
            .map((t) => t.aircraft_id)
            .filter((id) => id);

          if (aircraftIds.length > 0) {
            const enrichedAircraft = await fetchAircraftWithCache(
              postgrest,
              aircraftIds,
              activeFBO
            );

            // Merge enriched aircraft data back into tenants
            holdingAreaTenants = holdingAreaTenants.map((tenant) => ({
              ...tenant,
              aircraft:
                enrichedAircraft.find(({ id }) => tenant.aircraft_id === id) ??
                tenant.aircraft,
            }));
          }
        } catch (err) {
          console.error("Failed to load holding area entries:", err);
        }
      }

      zHangars.push({
        id: HOLDING_AREA_ID,
        location_id: "", // Virtual hangar, no physical location
        fbo_id: activeFBO?.id || "",
        name: "Holding Area",
        width: 100,
        depth: 300,
        height: 50,
        left_door: 0,
        right_door: 0,
        wall_spacing_left: 0,
        wall_spacing_back: 0,
        wall_spacing_right: 0,
        stack: {
          id: "holding-area-stack",
          name: "current",
          isReference: false,
          tenants: holdingAreaTenants,
          movableObstacles: [],
          options: {
            wall_spacing_left: 0,
            wall_spacing_back: 0,
            wall_spacing_right: 0,
            horizontal_spacing: 0,
            vertical_spacing: 0,
            overlap: false,
            tug_spacing: 0,
            density_threshold: 0,
            corner_strategy: [],
            xy_weights: [],
            towing_equipment_id: "",
          },
        },
        stacks: [
          {
            id: "holding-area-stack",
            name: "current",
            isReference: false,
            tenants: holdingAreaTenants,
            movableObstacles: [],
            options: {
              wall_spacing_left: 0,
              wall_spacing_back: 0,
              wall_spacing_right: 0,
              horizontal_spacing: 0,
              vertical_spacing: 0,
              overlap: false,
              tug_spacing: 0,
              density_threshold: 0,
              corner_strategy: [],
              xy_weights: [],
              towing_equipment_id: "",
            },
          },
        ],
        obstacles: [],
        garageDoors: [],
        created_at: new Date().toISOString(),
        last_stacked: new Date().toISOString(),
      });
      setHangars(zHangars);
      setReady(true);
    })();
  }, []);

  return {
    // Ready once fetch is complete - hangars array may be smaller than requested
    // if some IDs don't exist (e.g., invalid/deleted hangars)
    ready: ready && Boolean(hangars),
    getHangar: (hangar_id: string) => hangars?.find((h) => h.id === hangar_id),
    hangars: hangars ?? [],
    setHangar,
    setHangars,
    // setHangars: (newHangars: Hangar[]) => {
    //   // make sure there are no duplicate tenants
    //   setHangars(
    //     newHangars.map((h) => ({
    //       ...h,
    //       stack: {
    //         ...h.stack,
    //         tenants: h.stack.tenants.filter(
    //           (t, i, self) => self.findIndex((s) => s.id === t.id) === i
    //         ),
    //       },
    //     }))
    //   );
    // },
    history: {
      undo,
    },
    isReference,
  };
};
